import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { StarRow } from "./IndustryDirectory";
import { buildVendorRefs } from "../lib/externalRefs";
import {
  ArrowLeft, MapPin, ExternalLink, Star, Shield, ShieldAlert, Sparkles,
  MessageSquareText, ThumbsUp, ThumbsDown, Building2, Globe, Loader2,
  Users, Quote, Image as ImageIcon, FileText, Mail, Linkedin,
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "references", label: "References & Fraud check" },
  { id: "reviews", label: "Member reviews" },
];

export default function IndustryVendorDetail() {
  const { slug } = useParams();
  const [vendor, setVendor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [myReview, setMyReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  const reload = async () => {
    try {
      const [v, r, mine] = await Promise.all([
        api.get(`/industry/vendors/${slug}`),
        api.get(`/industry/vendors/${slug}/reviews`),
        api.get(`/industry/vendors/${slug}/reviews/mine`).catch(() => ({ data: null })),
      ]);
      setVendor(v.data);
      setReviews(r.data || []);
      setMyReview(mine.data || null);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [slug]);

  const refs = useMemo(() => (vendor ? buildVendorRefs(vendor) : []), [vendor]);

  if (loading) {
    return (
      <AppShell title="Vendor">
        <p className="text-sm text-muted-foreground">Loading vendor…</p>
      </AppShell>
    );
  }
  if (!vendor) {
    return (
      <AppShell title="Vendor">
        <p className="text-sm text-muted-foreground">Vendor not found.</p>
      </AppShell>
    );
  }

  const stars = Number(vendor.avg_member_rating || 0);
  const rc = Number(vendor.member_review_count || 0);
  const loc = vendor.location || {};
  const locLabel = [loc.city, loc.state || loc.country].filter(Boolean).join(", ");

  return (
    <AppShell title={vendor.name}>
      <div className="w-full">
        <Link to="/industry-directory" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground transition" data-testid="industry-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Industry Directory
        </Link>

        {/* Header */}
        <div className="mt-4 card-elevated overflow-hidden">
          {(vendor.cover_image_url || vendor.logo_url) && (
            <div className="relative h-40 md:h-52 bg-gradient-to-br from-shell-side/90 to-shell-side" data-testid="industry-vendor-cover">
              {vendor.cover_image_url && (
                <img src={vendor.cover_image_url} alt="" className="w-full h-full object-cover" />
              )}
              {vendor.logo_url && (
                <div className="absolute -bottom-6 left-6 w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white shadow-md border border-shell-border overflow-hidden grid place-items-center" data-testid="industry-vendor-logo">
                  <img src={vendor.logo_url} alt={`${vendor.name} logo`} className="w-full h-full object-contain p-2" />
                </div>
              )}
            </div>
          )}
          <div className={`p-6 md:p-7 ${vendor.logo_url ? "pt-10" : ""}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                {vendor.subcategory && (
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">
                    {vendor.subcategory}
                  </span>
                )}
                {vendor.featured && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-pink-50 text-pink-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">
                    <Star className="w-2.5 h-2.5 fill-current" /> Featured
                  </span>
                )}
                {vendor.verified && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">
                    <Shield className="w-2.5 h-2.5" /> Institute-verified
                  </span>
                )}
                {vendor.demo && (
                  <span className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]" data-testid="industry-vendor-demo-badge">
                    Demo
                  </span>
                )}
              </div>
              <h1 className="font-display text-3xl md:text-4xl mt-2" data-testid="industry-vendor-name">{vendor.name}</h1>
              <p className="mt-2 text-muted-foreground max-w-3xl">{vendor.description}</p>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-muted-foreground">
                {locLabel && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{locLabel}</span>}
                {vendor.website && (
                  <a href={vendor.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:text-primary/80" data-testid="industry-vendor-website">
                    <Globe className="w-3.5 h-3.5" /> Website <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>

            <div className="text-right">
              <StarRow rating={stars} reviews={rc} size={18} />
            </div>
          </div>

          {/* Services + specialties */}
          {(vendor.services?.length > 0 || vendor.specialties?.length > 0 || vendor.certifications?.length > 0 || vendor.min_run_size) && (
            <div className="mt-5 grid md:grid-cols-2 gap-4 text-sm">
              {vendor.services?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Services</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {vendor.services.map((s) => <TagPill key={s} label={s} />)}
                  </div>
                </div>
              )}
              {vendor.specialties?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Specialties</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {vendor.specialties.map((s) => <TagPill key={s} label={s} />)}
                  </div>
                </div>
              )}
              {vendor.certifications?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Certifications</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {vendor.certifications.map((s) => <TagPill key={s} label={s} tone="emerald" />)}
                  </div>
                </div>
              )}
              {vendor.min_run_size && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Minimum run</p>
                  <p className="mt-1 font-semibold text-sm">{vendor.min_run_size}</p>
                </div>
              )}
            </div>
          )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 border-b border-shell-border flex items-center gap-1 flex-wrap" data-testid="industry-vendor-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`industry-tab-${t.id}`}
              className={[
                "px-4 py-2.5 text-[13px] font-semibold border-b-2 transition",
                tab === t.id
                  ? "border-shell-accent text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >
              {t.label}
              {t.id === "reviews" && rc > 0 && (
                <span className="ml-1.5 text-[10px] font-bold text-muted-foreground">({rc})</span>
              )}
            </button>
          ))}
        </div>

        <div className="mt-5">
          {tab === "overview" && <OverviewPanel vendor={vendor} />}
          {tab === "references" && <ReferencesPanel refs={refs} vendor={vendor} />}
          {tab === "reviews" && (
            <ReviewsPanel
              slug={slug}
              vendor={vendor}
              reviews={reviews}
              myReview={myReview}
              onSaved={reload}
            />
          )}
        </div>
      </div>
    </AppShell>
  );
}

function TagPill({ label, tone }) {
  const t = tone === "emerald"
    ? "bg-emerald-50 text-emerald-800 border-emerald-100"
    : "bg-shell-border/20 text-foreground/80 border-shell-border";
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11.5px] font-medium ${t}`}>{label}</span>
  );
}

function OverviewPanel({ vendor }) {
  const clients = vendor.clients || [];
  const gallery = vendor.gallery || [];
  const testimonials = vendor.references || [];
  const team = vendor.team || [];
  const caseStudies = vendor.case_studies || [];
  const anyRichContent = clients.length + gallery.length + testimonials.length + team.length + caseStudies.length > 0;

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card-elevated p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">About</p>
          <p className="mt-2 text-sm leading-relaxed text-foreground/85 whitespace-pre-line break-words">
            {vendor.description}
          </p>
        </div>
        <div className="card-elevated p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Before you sign</p>
          <ul className="mt-3 space-y-2.5 text-[13px] leading-relaxed text-foreground/85">
            <li className="flex items-start gap-2">
              <ShieldAlert className="w-4 h-4 text-pink-500 mt-0.5 shrink-0" />
              <span className="min-w-0 break-words">
                Check the <b>References &amp; Fraud check</b> tab — BBB, FDA, USDA, and FTC search links are one click away.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <MessageSquareText className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <span className="min-w-0 break-words">
                Read what other Institute founders have said in <b>Member reviews</b>.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-shell-accent mt-0.5 shrink-0" />
              <span className="min-w-0 break-words">
                Ask <Link to="/assistant" className="text-primary underline">Morgan</Link> what red flags to look for in this vendor category.
              </span>
            </li>
          </ul>
        </div>
      </div>

      {clients.length > 0 && <ClientsSection clients={clients} />}
      {caseStudies.length > 0 && <CaseStudiesSection items={caseStudies} />}
      {testimonials.length > 0 && <TestimonialsSection items={testimonials} />}
      {team.length > 0 && <TeamSection members={team} />}
      {gallery.length > 0 && <GallerySection items={gallery} />}

      {!anyRichContent && (
        <p className="text-[13px] text-muted-foreground italic" data-testid="industry-vendor-empty-extras">
          This vendor hasn't added clients, gallery images, references, team, or case studies yet.
        </p>
      )}
    </div>
  );
}

function SectionHeader({ icon: Icon, kicker, title, subtitle }) {
  return (
    <div className="mb-3">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary inline-flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />} {kicker}
      </p>
      <h3 className="font-display text-xl mt-1">{title}</h3>
      {subtitle && <p className="text-[13px] text-muted-foreground mt-1">{subtitle}</p>}
    </div>
  );
}

function ClientsSection({ clients }) {
  return (
    <section className="card-elevated p-5" data-testid="industry-vendor-clients">
      <SectionHeader icon={Building2} kicker="Selected clients" title="Brands they've worked with" />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {clients.map((c, i) => {
          const inner = (
            <>
              {c.logo_url ? (
                <div className="w-full h-16 flex items-center justify-center p-2 mb-2">
                  <img src={c.logo_url} alt={c.name} className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-shell-side text-white grid place-items-center font-bold text-xs">
                  {c.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                </div>
              )}
              <p className="text-sm font-semibold text-foreground text-center truncate" title={c.name}>{c.name}</p>
            </>
          );
          const wrapClass = "rounded-xl border border-shell-border bg-white p-3 flex flex-col items-center justify-center transition hover:border-primary/40";
          return c.website ? (
            <a
              key={i}
              href={c.website.startsWith("http") ? c.website : `https://${c.website}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`industry-vendor-client-${i}`}
              className={wrapClass}
            >
              {inner}
            </a>
          ) : (
            <div key={i} data-testid={`industry-vendor-client-${i}`} className={wrapClass}>{inner}</div>
          );
        })}
      </div>
    </section>
  );
}

function CaseStudiesSection({ items }) {
  return (
    <section className="card-elevated p-5" data-testid="industry-vendor-case-studies">
      <SectionHeader icon={FileText} kicker="Case studies" title="Recent projects" />
      <ul className="space-y-3">
        {items.map((c, i) => (
          <li key={i} className="rounded-xl border border-shell-border bg-shell-muted/30 p-4" data-testid={`industry-vendor-case-study-${i}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <h4 className="font-display text-base text-foreground">{c.title}</h4>
              {c.url && (
                <a
                  href={c.url.startsWith("http") ? c.url : `https://${c.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
                >
                  <ExternalLink className="w-3 h-3" /> Read more
                </a>
              )}
            </div>
            {c.summary && <p className="text-[13px] text-foreground/80 mt-1.5 whitespace-pre-line">{c.summary}</p>}
          </li>
        ))}
      </ul>
    </section>
  );
}

function TestimonialsSection({ items }) {
  return (
    <section className="card-elevated p-5" data-testid="industry-vendor-testimonials">
      <SectionHeader icon={Quote} kicker="References" title="What clients say" />
      <ul className="grid md:grid-cols-2 gap-3">
        {items.map((r, i) => (
          <li key={i} className="rounded-xl border border-shell-border bg-white p-4 relative" data-testid={`industry-vendor-testimonial-${i}`}>
            <Quote className="w-6 h-6 text-primary/25 -mb-1" />
            <p className="text-[14px] leading-relaxed text-foreground/90">{r.quote}</p>
            <div className="mt-3 pt-3 border-t border-shell-border">
              <p className="font-semibold text-sm">{r.name}</p>
              <p className="text-[11px] text-muted-foreground">
                {[r.role, r.company].filter(Boolean).join(" · ") || " "}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function TeamSection({ members }) {
  return (
    <section className="card-elevated p-5" data-testid="industry-vendor-team">
      <SectionHeader icon={Users} kicker="Team" title="Who you'll work with" />
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
        {members.map((m, i) => {
          const initials = m.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
          return (
            <div key={i} className="rounded-xl border border-shell-border bg-white p-4 flex items-center gap-3" data-testid={`industry-vendor-team-${i}`}>
              {m.photo_url ? (
                <img src={m.photo_url} alt={m.name} className="w-12 h-12 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-shell-side text-white grid place-items-center font-bold text-xs shrink-0">
                  {initials}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm truncate">{m.name}</p>
                {m.title && <p className="text-[11px] text-muted-foreground truncate">{m.title}</p>}
                <div className="mt-1 flex items-center gap-2">
                  {m.email && (
                    <a href={`mailto:${m.email}`} title={m.email} className="text-muted-foreground hover:text-primary transition">
                      <Mail className="w-3.5 h-3.5" />
                    </a>
                  )}
                  {m.linkedin_url && (
                    <a href={m.linkedin_url.startsWith("http") ? m.linkedin_url : `https://${m.linkedin_url}`} target="_blank" rel="noopener noreferrer" title="LinkedIn" className="text-muted-foreground hover:text-primary transition">
                      <Linkedin className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function GallerySection({ items }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  return (
    <section className="card-elevated p-5" data-testid="industry-vendor-gallery">
      <SectionHeader icon={ImageIcon} kicker="Gallery" title="Their work in pictures" />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {items.map((g, i) => (
          <button
            type="button"
            key={i}
            onClick={() => setLightboxIndex(i)}
            data-testid={`industry-vendor-gallery-${i}`}
            className="relative aspect-square rounded-lg overflow-hidden border border-shell-border bg-shell-muted/40 hover:opacity-90 transition"
            title={g.caption}
          >
            <img src={g.image_url} alt={g.caption || ""} className="w-full h-full object-cover" />
            {g.caption && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-[10px] text-white text-left line-clamp-2">
                {g.caption}
              </div>
            )}
          </button>
        ))}
      </div>
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-6"
          onClick={() => setLightboxIndex(null)}
          data-testid="industry-vendor-gallery-lightbox"
        >
          <div className="max-w-4xl max-h-full">
            <img src={items[lightboxIndex].image_url} alt="" className="max-w-full max-h-[80vh] object-contain rounded-lg" />
            {items[lightboxIndex].caption && (
              <p className="mt-3 text-white/90 text-sm text-center">{items[lightboxIndex].caption}</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function ReferencesPanel({ refs, vendor }) {
  const reputation = refs.filter((r) => r.kind !== "fraud");
  const fraud = refs.filter((r) => r.kind === "fraud");
  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-shell-side flex items-center gap-1.5">
          <Building2 className="w-3 h-3" /> Reputation & reviews
        </p>
        <p className="text-[13px] text-muted-foreground mt-1">
          Pre-filled searches on <b>{vendor.name}</b>. Each link opens the source site in a new tab.
        </p>
        <div className="mt-3 grid md:grid-cols-2 gap-3" data-testid="industry-refs-reputation">
          {reputation.map((r) => <RefCard key={r.key} r={r} />)}
        </div>
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-red-700 flex items-center gap-1.5">
          <ShieldAlert className="w-3 h-3" /> Fraud & regulatory check
        </p>
        <p className="text-[13px] text-muted-foreground mt-1">
          One-click searches on the government databases where compliance history lives.
        </p>
        <div className="mt-3 grid md:grid-cols-3 gap-3" data-testid="industry-refs-fraud">
          {fraud.map((r) => <RefCard key={r.key} r={r} />)}
        </div>
      </div>
    </div>
  );
}

function RefCard({ r }) {
  const isFraud = r.kind === "fraud";
  return (
    <a
      href={r.url}
      target="_blank"
      rel="noreferrer"
      className={[
        "rounded-xl border p-4 hover:shadow-md transition group flex flex-col gap-1",
        isFraud
          ? "border-red-200 bg-red-50/40 hover:border-red-300"
          : "border-shell-border bg-white hover:border-primary/40",
      ].join(" ")}
      data-testid={`industry-ref-${r.key}`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className={`text-sm font-semibold ${isFraud ? "text-red-800" : "text-foreground"}`}>{r.label}</p>
        <ExternalLink className={`w-3.5 h-3.5 ${isFraud ? "text-red-500" : "text-muted-foreground group-hover:text-primary"}`} />
      </div>
      <p className="text-[12px] text-muted-foreground">{r.describe}</p>
      {r.isCustom && (
        <p className="text-[11px] text-emerald-700 mt-1">Institute-curated link</p>
      )}
      {typeof r.rating === "number" && r.rating > 0 && (
        <div className="mt-1"><StarRow rating={r.rating} reviews={0} size={12} /></div>
      )}
    </a>
  );
}

function ReviewsPanel({ slug, vendor, reviews, myReview, onSaved }) {
  const [rating, setRating] = useState(myReview?.rating || 0);
  const [hover, setHover] = useState(0);
  const [headline, setHeadline] = useState(myReview?.headline || "");
  const [body, setBody] = useState(myReview?.body || "");
  const [recommend, setRecommend] = useState(
    myReview?.would_recommend === false ? "no" : myReview?.would_recommend ? "yes" : ""
  );
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (rating < 1) {
      toast.error("Give a star rating first.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/industry/vendors/${slug}/reviews`, {
        rating,
        headline,
        body,
        would_recommend: recommend === "" ? null : recommend === "yes",
      });
      toast.success(myReview ? "Review updated" : "Review posted");
      onSaved?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6">
      <div>
        {reviews.length === 0 ? (
          <div className="card-elevated p-8 text-center" data-testid="industry-reviews-empty">
            <MessageSquareText className="w-8 h-8 mx-auto text-muted-foreground" />
            <p className="mt-2 font-display text-lg">No reviews yet</p>
            <p className="text-sm text-muted-foreground max-w-md mx-auto mt-1">
              Be the first Institute founder to share what it's like working with {vendor.name}.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="industry-reviews-list">
            {reviews.map((r) => <ReviewCard key={r.id} r={r} />)}
          </ul>
        )}
      </div>

      <aside className="card-elevated p-5 h-fit sticky top-4" data-testid="industry-review-form">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">
          {myReview ? "Edit your review" : "Write a review"}
        </p>
        <p className="text-[12px] text-muted-foreground mt-1">
          Your name and headshot will be shown next to your review.
        </p>

        {/* Stars */}
        <div className="mt-3 flex items-center gap-1" onMouseLeave={() => setHover(0)} data-testid="industry-review-rating">
          {[1, 2, 3, 4, 5].map((n) => {
            const active = (hover || rating) >= n;
            return (
              <button
                key={n}
                type="button"
                onMouseEnter={() => setHover(n)}
                onClick={() => setRating(n)}
                aria-label={`${n} star${n === 1 ? "" : "s"}`}
                data-testid={`industry-review-star-${n}`}
                className="p-0.5"
              >
                <Star className={`w-6 h-6 ${active ? "text-pink-500 fill-current" : "text-slate-300"}`} />
              </button>
            );
          })}
          {rating > 0 && <span className="ml-2 text-xs text-muted-foreground">{rating}/5</span>}
        </div>

        <label className="mt-3 block">
          <span className="text-xs font-semibold">Headline</span>
          <input
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            maxLength={140}
            placeholder="e.g. Solid partner but slow on tolling changes"
            data-testid="industry-review-headline"
            className="mt-1 w-full rounded-lg bg-white border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary"
          />
        </label>
        <label className="mt-3 block">
          <span className="text-xs font-semibold">Your review</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            maxLength={2000}
            placeholder="What went well? What would you warn other founders about?"
            data-testid="industry-review-body"
            className="mt-1 w-full rounded-lg bg-white border border-shell-border px-3 py-2 text-sm resize-y focus:outline-none focus:border-primary"
          />
        </label>
        <div className="mt-3">
          <span className="text-xs font-semibold">Would you recommend them?</span>
          <div className="mt-1 flex gap-2">
            <RecPill value="yes" active={recommend === "yes"} setActive={setRecommend} icon={ThumbsUp} label="Yes" tone="emerald" />
            <RecPill value="no" active={recommend === "no"} setActive={setRecommend} icon={ThumbsDown} label="No" tone="red" />
          </div>
        </div>

        <button
          type="button"
          onClick={submit}
          disabled={saving}
          data-testid="industry-review-submit"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-xs font-bold uppercase tracking-[0.14em] text-white shadow hover:shadow-md transition disabled:opacity-70"
          style={{ background: "linear-gradient(135deg, #D81B60 0%, #6A0DAD 100%)" }}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          {saving ? "Saving…" : myReview ? "Update review" : "Post review"}
        </button>
      </aside>
    </div>
  );
}

function RecPill({ value, active, setActive, icon: Icon, label, tone }) {
  const toneCls = tone === "emerald"
    ? "border-emerald-200 text-emerald-700 bg-emerald-50"
    : "border-red-200 text-red-700 bg-red-50";
  return (
    <button
      type="button"
      onClick={() => setActive(active ? "" : value)}
      data-testid={`industry-review-rec-${value}`}
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold border transition",
        active ? toneCls : "border-shell-border bg-white text-muted-foreground hover:text-foreground",
      ].join(" ")}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function ReviewCard({ r }) {
  const initials = (r.author_name || "M").slice(0, 2).toUpperCase();
  return (
    <li className="card-elevated p-4" data-testid={`industry-review-${r.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 shrink-0 rounded-full bg-primary/10 text-primary font-bold text-sm grid place-items-center overflow-hidden">
          {r.author_picture ? (
            <img src={r.author_picture} alt="" className="w-full h-full object-cover" />
          ) : (
            initials
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <p className="font-semibold text-sm">{r.author_name || "Member"}</p>
            <StarRow rating={Number(r.rating)} reviews={0} size={12} />
            {r.would_recommend === true && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">
                <ThumbsUp className="w-2.5 h-2.5" /> Recommends
              </span>
            )}
            {r.would_recommend === false && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">
                <ThumbsDown className="w-2.5 h-2.5" /> Wouldn't recommend
              </span>
            )}
          </div>
          {r.headline && <p className="mt-1.5 font-semibold text-[14.5px]">{r.headline}</p>}
          {r.body && <p className="mt-1 text-[13px] text-foreground/85 whitespace-pre-line">{r.body}</p>}
        </div>
      </div>
    </li>
  );
}
